import { Button } from "@bowfast/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bowfast/ui/components/dialog";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import { useEffect, useState } from "react";
import type { SkillCategory } from "~/pages/skills/skills.types";

interface CategoryDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  category?: SkillCategory;
  onSave: (name: string, description: string | null) => void;
  isSaving?: boolean;
}

export function CategoryDialog({ open, onOpenChange, category, onSave, isSaving }: Readonly<CategoryDialogProps>) {
  const isEdit = !!category;
  const [name, setName] = useState(category?.name ?? "");
  const [description, setDescription] = useState(category?.description ?? "");

  useEffect(() => {
    if (open) {
      setName(category?.name ?? "");
      setDescription(category?.description ?? "");
    }
  }, [open, category]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim(), description.trim() || null);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit category" : "New skill category"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the name or description of this category."
              : "Group related skills into a named category — e.g. Navigation, Boat Handling, Safety."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Category name</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Navigation"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cat-desc">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="cat-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What skills belong in this category?"
            />
          </div>

          <DialogFooter showCloseButton>
            <Button type="submit" {...(isSaving || !name.trim() ? { disabled: true } : {})}>
              {isEdit ? "Save changes" : "Create category"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
